/**
 * Piezas compartidas por las tres pantallas del circuito de acceso.
 *
 * Viven juntas porque las tres hacen lo mismo con distinto marco: pedir una
 * contrasena mostrando cuanto le falta, y —del lado del administrador— mostrar
 * un enlace listo para copiar.
 */

import clsx from 'clsx';
import { useState, type ReactNode } from 'react';

import { IconAlert, IconCheck } from '@/components/shell/icons';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { COLOR_DE_FUERZA, medirFuerza } from '@/views/acceso/fuerza';

// ---------------------------------------------------------------------------
// Iconos locales
// ---------------------------------------------------------------------------
// Van aca y no en `components/shell/icons.tsx` para no tocar un archivo
// compartido por un par de glifos que solo usa esta pantalla.
function IconOjo({ abierto }: { abierto: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[1.05rem] w-[1.05rem]"
      aria-hidden="true"
    >
      {abierto ? (
        <>
          <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      ) : (
        <>
          <path d="M3 3l18 18" />
          <path d="M10.6 5.2A9.9 9.9 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.1" />
          <path d="M6.2 6.7A17.4 17.4 0 0 0 2 12s3.6 7 10 7c1.6 0 3-.3 4.2-.8" />
          <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
        </>
      )}
    </svg>
  );
}

export function IconCopiar() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/** El globo de WhatsApp. Va acá por lo mismo que el ojo: lo usa esta pantalla. */
function IconWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M12.04 2C6.6 2 2.2 6.4 2.2 11.84c0 1.74.46 3.44 1.32 4.94L2 22l5.36-1.4a9.8 9.8 0 0 0 4.68 1.19h.01c5.43 0 9.84-4.4 9.84-9.84 0-2.63-1.02-5.1-2.88-6.96A9.78 9.78 0 0 0 12.04 2Zm0 1.8c2.14 0 4.15.84 5.67 2.35a7.96 7.96 0 0 1 2.35 5.68c0 4.44-3.6 8.04-8.05 8.04a8.1 8.1 0 0 1-4.09-1.11l-.29-.17-3.03.79.81-2.95-.19-.3a8 8 0 0 1-1.24-4.3c0-4.44 3.61-8.04 8.06-8.04Zm-3.5 4.06c-.16 0-.43.06-.65.31-.23.25-.86.85-.86 2.06 0 1.22.88 2.39 1 2.56.13.16 1.72 2.72 4.25 3.7.6.24 1.06.38 1.42.48.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.19.2-.58.2-1.08.15-1.19-.06-.1-.23-.16-.48-.28-.25-.13-1.47-.73-1.7-.81-.23-.09-.4-.13-.56.12-.17.25-.65.81-.79.98-.15.16-.3.19-.55.06-.25-.12-1.06-.39-2.02-1.24-.75-.67-1.25-1.49-1.4-1.74-.14-.25-.01-.39.11-.51.11-.11.25-.29.37-.44.13-.15.17-.25.25-.42.09-.16.04-.31-.02-.44-.06-.12-.55-1.35-.76-1.84-.2-.48-.4-.42-.55-.42h-.46Z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Marco de las pantallas publicas
// ---------------------------------------------------------------------------
/** Tarjeta centrada con la marca arriba. Las dos pantallas publicas la comparten. */
export function MarcoPublico({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-svh place-items-center bg-app px-4 py-10">
      {/* El selector de tema va acá y no dentro de cada pantalla: `fixed` lo
          ancla al viewport sin depender de que la tarjeta sea `relative`. */}
      <div className="fixed right-4 top-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-7 flex items-center justify-center gap-3">
          <span
            aria-hidden="true"
            className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-lg font-bold text-accent-contrast"
          >
            N
          </span>
          <span className="text-lg font-semibold tracking-tight">Núcleo</span>
        </div>
        <div className="rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-7">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avisos
// ---------------------------------------------------------------------------
export function Aviso({
  tono,
  children,
}: {
  tono: 'error' | 'exito' | 'info';
  children: ReactNode;
}) {
  const estilos = {
    error: 'border-danger/30 bg-danger-soft',
    exito: 'border-success/30 bg-success-soft',
    info: 'border-line bg-app-subtle',
  } as const;
  return (
    <div
      role={tono === 'error' ? 'alert' : 'status'}
      className={clsx('flex items-start gap-2.5 rounded-lg border p-3 text-sm', estilos[tono])}
    >
      {tono === 'error' && <IconAlert className="h-5 w-5 shrink-0 text-danger" />}
      {tono === 'exito' && <IconCheck className="h-5 w-5 shrink-0 text-success" />}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campo de contrasena con indicador de fuerza
// ---------------------------------------------------------------------------
export function CampoContrasena({
  id,
  label,
  valor,
  onChange,
  autoComplete,
  mostrarFuerza = false,
  email = '',
  nombre = '',
  autoFocus = false,
}: {
  id: string;
  label: string;
  valor: string;
  onChange: (valor: string) => void;
  autoComplete: string;
  /** Sólo en el campo donde se ELIGE la clave; repetirlo dos veces es ruido. */
  mostrarFuerza?: boolean;
  email?: string;
  nombre?: string;
  autoFocus?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const fuerza = medirFuerza(valor, email, nombre);

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          name={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          required
          value={valor}
          onChange={(evento) => {
            onChange(evento.target.value);
          }}
          className="h-11 w-full rounded-md border border-line bg-surface pl-3 pr-11 outline-none transition-colors focus:border-accent"
          {...(mostrarFuerza ? { 'aria-describedby': `${id}-fuerza` } : {})}
        />
        <button
          type="button"
          onClick={() => {
            setVisible((previo) => !previo);
          }}
          className="absolute right-1 top-1 grid h-9 w-9 place-items-center rounded-md text-fg-muted transition-colors hover:bg-fg/5 hover:text-fg"
          aria-label={visible ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
          title={visible ? 'Ocultar' : 'Mostrar'}
        >
          <IconOjo abierto={visible} />
        </button>
      </div>

      {mostrarFuerza && valor !== '' && (
        <div id={`${id}-fuerza`} className="mt-2" aria-live="polite">
          <div className="flex items-center gap-2">
            <div className="flex h-1.5 flex-1 gap-1" aria-hidden="true">
              {[1, 2, 3, 4].map((tramo) => (
                <span
                  key={tramo}
                  className={clsx(
                    'flex-1 rounded-full transition-colors',
                    tramo <= fuerza.puntaje ? COLOR_DE_FUERZA[fuerza.nivel] : 'bg-line',
                  )}
                />
              ))}
            </div>
            <span
              className={clsx(
                'shrink-0 text-xs font-medium',
                fuerza.nivel === 'debil' ? 'text-danger' : 'text-fg-muted',
              )}
            >
              {fuerza.etiqueta}
            </span>
          </div>
          {fuerza.consejos.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs text-fg-muted">
              {fuerza.consejos.map((consejo) => (
                <li key={consejo}>· {consejo}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copiar al portapapeles
// ---------------------------------------------------------------------------
/**
 * Copia texto, con camino de reserva.
 *
 * `navigator.clipboard` no existe fuera de un contexto seguro (http sobre una
 * IP, que es como se prueba una instalacion nueva) y ADEMAS puede rechazar por
 * permisos aunque exista. Los dos casos caen a `execCommand`, y si tampoco, el
 * llamador avisa para que la persona copie a mano: copiar no puede fallar en
 * silencio en la pantalla que resuelve la entrega.
 */
async function copiarTexto(texto: string): Promise<boolean> {
  if (navigator.clipboard !== undefined && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(texto);
      return true;
    } catch {
      // Sigue por el camino viejo.
    }
  }

  const area = document.createElement('textarea');
  area.value = texto;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(area);
  return ok;
}

/** Un boton de la fila de envio, con su «Copiado» de dos segundos y medio. */
function BotonDeCopia({
  texto,
  etiqueta,
  etiquetaHecha,
  variante,
  onFallo,
}: {
  texto: string;
  etiqueta: string;
  etiquetaHecha: string;
  variante: 'primary' | 'secondary';
  onFallo: () => void;
}) {
  const [hecho, setHecho] = useState(false);

  return (
    <button
      type="button"
      onClick={() => {
        void copiarTexto(texto).then((ok) => {
          if (!ok) {
            onFallo();
            return;
          }
          setHecho(true);
          window.setTimeout(() => {
            setHecho(false);
          }, 2500);
        });
      }}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
        hecho
          ? 'bg-success text-white'
          : variante === 'primary'
            ? 'bg-accent text-accent-contrast hover:bg-accent-hover'
            : 'border border-line bg-surface hover:bg-surface-raised',
      )}
    >
      {hecho ? <IconCheck className="h-4 w-4" /> : <IconCopiar />}
      {hecho ? etiquetaHecha : etiqueta}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Enlace copiable
// ---------------------------------------------------------------------------
/**
 * El enlace de invitacion con las tres formas de hacerlo llegar.
 *
 * Es la pieza que hace que el circuito funcione SIN servidor de correo, que es
 * el escenario del dia de la entrega. Por eso se muestra grande y con los
 * botones al lado, no escondida detras de un «ver detalles»: no es el plan B.
 *
 * Los tres botones —los que el manual (06 §6.1) promete— no son decoracion:
 *
 * * **Copiar el enlace** es lo minimo, y sirve para pegarlo donde sea.
 * * **Copiar el mensaje** copia el texto ya redactado. Sin esto, el
 *   administrador pega una URL de 120 caracteres en un WhatsApp, sin decir de
 *   que es ni de parte de quien, y la persona que la recibe —que muchas veces
 *   es un propietario mayor— no la abre porque parece una estafa. Redactarlo a
 *   mano veinte veces tampoco es una opcion.
 * * **Enviar por WhatsApp** abre WhatsApp con ese texto puesto. Es el canal por
 *   el que esto se manda de verdad en una administracion argentina.
 *
 * `mensaje` es opcional: donde no hay un texto que armar (una pantalla que solo
 * muestra una URL) la pieza sigue siendo el boton de copiar de siempre.
 */
export function EnlaceCopiable({
  enlace,
  etiqueta,
  mensaje,
}: {
  enlace: string;
  etiqueta?: string;
  /** Texto ya redactado para mandarle a la persona. Sin esto va sólo «Copiar el enlace». */
  mensaje?: string;
}) {
  const [fallo, setFallo] = useState(false);
  const avisarFallo = () => {
    setFallo(true);
  };

  return (
    <div>
      {etiqueta !== undefined && <p className="mb-1.5 text-sm font-medium">{etiqueta}</p>}
      <input
        readOnly
        value={enlace}
        onFocus={(evento) => {
          evento.target.select();
        }}
        aria-label="Enlace de acceso"
        className="w-full rounded-md border border-line bg-app-subtle px-3 py-2 font-mono text-xs outline-none focus:border-accent"
      />

      <div className="mt-2 flex flex-wrap gap-2">
        <BotonDeCopia
          texto={enlace}
          etiqueta="Copiar el enlace"
          etiquetaHecha="Enlace copiado"
          variante={mensaje === undefined ? 'primary' : 'secondary'}
          onFallo={avisarFallo}
        />

        {mensaje !== undefined && (
          <>
            <BotonDeCopia
              texto={mensaje}
              etiqueta="Copiar el mensaje"
              etiquetaHecha="Mensaje copiado"
              variante="primary"
              onFallo={avisarFallo}
            />
            {/* `wa.me` sin numero abre WhatsApp con el texto puesto y deja
                elegir a quien mandarselo: el sistema no guarda el telefono de
                cada persona, y pedirlo para esto seria pedir un dato de mas.
                `noopener` porque se abre otra pestana. NO manda nada solo: deja
                el mensaje escrito y la persona aprieta enviar. */}
            <a
              href={`https://wa.me/?text=${encodeURIComponent(mensaje)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium transition-colors hover:bg-surface-raised"
            >
              <IconWhatsApp />
              Enviar por WhatsApp
            </a>
          </>
        )}
      </div>

      {mensaje !== undefined && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-fg-muted hover:text-fg">
            Ver el mensaje que se va a copiar
          </summary>
          <pre className="mt-1.5 whitespace-pre-wrap rounded-md border border-line bg-app-subtle px-3 py-2 text-xs text-fg-muted">
            {mensaje}
          </pre>
        </details>
      )}

      {fallo && (
        <p className="mt-1.5 text-xs text-danger">
          El navegador no dejó copiar automáticamente. Seleccioná el texto y copialo con Ctrl+C.
        </p>
      )}
    </div>
  );
}
